import { Icon } from '@iconify/react'
import { formatStatusLabel } from '@/lib/payment-status'
import type { CampaignSaleStatusChangeEntry } from '@/types/campaign-report'
import styles from './PedidoStatusHistorySection.module.css'

export interface PedidoStatusHistorySectionProps {
  entries: CampaignSaleStatusChangeEntry[] | undefined
  /** When false, omits the section heading (useful inside a modal). Default true. */
  showTitle?: boolean
}

function formatHistoryDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function actorTypeLabel(actorType: string | null | undefined, guard: string | null | undefined): string {
  const value = (actorType ?? guard ?? '').toLowerCase()
  if (value === 'admin') return 'Admin da plataforma'
  if (value === 'institution') return 'Titular da conta'
  return 'Usuário'
}

function sortNewestFirst(
  entries: CampaignSaleStatusChangeEntry[]
): CampaignSaleStatusChangeEntry[] {
  return [...entries].sort((a, b) => {
    const ta = a.changed_at ? Date.parse(a.changed_at) : 0
    const tb = b.changed_at ? Date.parse(b.changed_at) : 0
    return tb - ta
  })
}

export function PedidoStatusHistorySection({
  entries,
  showTitle = true,
}: PedidoStatusHistorySectionProps) {
  const list = sortNewestFirst(entries ?? [])

  return (
    <section
      className={styles.section}
      aria-labelledby={showTitle ? 'pedido-status-history-title' : undefined}
      aria-label={showTitle ? undefined : 'Histórico de status'}
    >
      {showTitle ? (
        <h3 id="pedido-status-history-title" className={styles.title}>
          <Icon icon="mdi:history" className={styles.titleIcon} aria-hidden />
          Histórico de status
        </h3>
      ) : null}

      {list.length === 0 ? (
        <p className={styles.empty} role="status">
          Nenhuma alteração de status registrada.
        </p>
      ) : (
        <ol className={styles.list}>
          {list.map((entry, index) => {
            const key = `${entry.changed_at ?? 'na'}-${entry.from ?? ''}-${entry.to ?? ''}-${index}`
            return (
              <li key={key} className={styles.item}>
                <div className={styles.transition}>
                  <span className={styles.from}>{formatStatusLabel(entry.from)}</span>
                  <Icon icon="mdi:arrow-right" className={styles.arrow} aria-hidden />
                  <span className={styles.to}>{formatStatusLabel(entry.to)}</span>
                </div>
                <dl className={styles.meta}>
                  <div className={styles.metaRow}>
                    <dt>Quando</dt>
                    <dd>{formatHistoryDateTime(entry.changed_at)}</dd>
                  </div>
                  <div className={styles.metaRow}>
                    <dt>Quem</dt>
                    <dd>
                      {entry.actor_display_name?.trim() || '—'}
                      <span className={styles.actorType}>
                        {' '}
                        ({actorTypeLabel(entry.actor_type, entry.guard)})
                      </span>
                      {entry.actor_email ? (
                        <span className={styles.email}> · {entry.actor_email}</span>
                      ) : null}
                    </dd>
                  </div>
                  <div className={styles.metaRow}>
                    <dt>Motivo</dt>
                    <dd>{entry.reason?.trim() || '—'}</dd>
                  </div>
                  <div className={styles.metaRow}>
                    <dt>IP</dt>
                    <dd>{entry.ip?.trim() || '—'}</dd>
                  </div>
                  {entry.user_agent ? (
                    <div className={styles.metaRow}>
                      <dt>Navegador</dt>
                      <dd className={styles.userAgent}>{entry.user_agent}</dd>
                    </div>
                  ) : null}
                </dl>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
